/* # LexwareContext - Version 5.5
1. SYNC: Übergabe des addSyncLog Callbacks an den LexwareService für detailliertes Echtzeit-Feedback.
2. ERROR HANDLING: Bessere Unterscheidung zwischen Netzwerk- und API-Fehlern.
*/
import React, { createContext, useContext, useReducer, useEffect, useState, useRef } from 'react';
import CryptoJS from 'crypto-js';
import { storageService } from '../services/storageService';
import { s3Service } from '../services/s3Service';
import { lexwareService } from '../services/lexwareService';
import { extractCustomersFromSales, parseCustomerImportCSV, parseLexwareCSV } from '../utils/csvParser';

const LexwareContext = createContext();

const STORAGE_KEY = 'GRT_CRM_MASTER_DATA';
const CONTACTS_KEY = 'GRT_CRM_CONTACTS';
const TEMPLATES_KEY = 'GRT_CRM_TEMPLATES';
const USERS_KEY = 'GRT_CRM_USERS';
const LOGS_KEY = 'GRT_CRM_LOGS';
const TASKS_KEY = 'GRT_CRM_TASKS';
const CUSTOMER_MASTER_KEY = 'GRT_CRM_CUSTOMER_MASTER';
const LAST_BACKUP_KEY = 'GRT_CRM_LAST_BACKUP';
const API_CONFIG_KEY = 'GRT_CRM_LEXWARE_API_CONFIG';

const ENCRYPTION_SECRET = import.meta.env.VITE_APP_SECRET || 'grt-crm-ultra-secure-key-2025';
const CLOUD_BUCKET = import.meta.env.VITE_S3_BUCKET || 'grt-crm';
const CLOUD_BACKUP_PATH = 'CRM Daten - nicht löschen/master_backup.json';

const encrypt = (text) => {
  if (!text) return '';
  return CryptoJS.AES.encrypt(text, ENCRYPTION_SECRET).toString();
};

const decrypt = (ciphertext) => {
  if (!ciphertext) return '';
  try {
    const bytes = CryptoJS.AES.decrypt(ciphertext, ENCRYPTION_SECRET);
    const plaintext = bytes.toString(CryptoJS.enc.Utf8);
    return plaintext || '';
  } catch (e) {
    console.error('Entschlüsselung fehlgeschlagen:', e);
    return '';
  }
};

const loadInitialApiConfig = () => {
  const stored = localStorage.getItem(API_CONFIG_KEY);
  if (!stored) return { apiKey: '', autoSync: false, startDate: '2025-01-01', lastSync: null };
  try {
    const config = JSON.parse(stored);
    if (config.apiKey && config.apiKey.length > 20 && config.apiKey.includes('U2FsdGVkX1')) {
      config.apiKey = decrypt(config.apiKey);
    }
    return config;
  } catch (e) {
    return { apiKey: '', autoSync: false, startDate: '2025-01-01', lastSync: null };
  }
};

const initialState = {
  isConnected: typeof navigator !== 'undefined' ? navigator.onLine : true,
  loading: false,
  error: null,
  isMockData: true,
  currentUser: null,
  customerMasterData: {},
  lastAutoBackup: localStorage.getItem(LAST_BACKUP_KEY) || null,
  autoBackupEnabled: true,
  cloudSyncStatus: 'idle',
  toast: null,
  lexwareApiConfig: loadInitialApiConfig(),
  data: {
    contacts: [],
    templates: [],
    customers: [],
    sales: [],
    users: [],
    logs: [],
    tasks: []
  }
};

function lexwareReducer(state, action) {
  switch (action.type) {
    case 'SET_CONNECTION_STATUS': return { ...state, isConnected: action.payload };
    case 'SET_LOADING': return { ...state, loading: action.payload };
    case 'SET_DATA': return { ...state, data: { ...state.data, ...action.payload }, isMockData: action.isMockData ?? state.isMockData, loading: false };
    case 'SET_CUSTOMER_MASTER': return { ...state, customerMasterData: action.payload };
    case 'ADD_LOG': return { ...state, data: { ...state.data, logs: [action.payload, ...state.data.logs].slice(0, 1000) } };
    case 'SET_CURRENT_USER': return { ...state, currentUser: action.payload };
    case 'SET_LAST_AUTO_BACKUP': 
      if (action.payload) localStorage.setItem(LAST_BACKUP_KEY, action.payload);
      return { ...state, lastAutoBackup: action.payload };
    case 'TOGGLE_AUTO_BACKUP': return { ...state, autoBackupEnabled: action.payload };
    case 'SET_CLOUD_SYNC_STATUS': return { ...state, cloudSyncStatus: action.payload };
    case 'SET_TOAST': return { ...state, toast: action.payload };
    case 'SET_API_CONFIG': 
      const encryptedConfig = { ...action.payload, apiKey: encrypt(action.payload.apiKey) };
      localStorage.setItem(API_CONFIG_KEY, JSON.stringify(encryptedConfig));
      return { ...state, lexwareApiConfig: action.payload };
    default: return state;
  }
}

export function LexwareProvider({ children }) {
  const [state, dispatch] = useReducer(lexwareReducer, initialState);
  const [lastCloudError, setLastCloudError] = useState(null);
  const [syncLogs, setSyncLogs] = useState([]);
  const autoSaveTimeoutRef = useRef(null);
  const initialSyncDone = useRef(false);

  const addSyncLog = (message, type = 'info') => {
    setSyncLogs(prev => [...prev, { 
      id: Date.now() + Math.random(), 
      timestamp: new Date().toISOString(), 
      message, 
      type 
    }]);
  };

  const clearSyncLogs = () => setSyncLogs([]);

  useEffect(() => {
    const handleOnline = () => {
      dispatch({ type: 'SET_CONNECTION_STATUS', payload: true });
      showToast('success', 'Internetverbindung wiederhergestellt');
      if (state.autoBackupEnabled && state.currentUser) saveToCloud(true);
    };
    const handleOffline = () => {
      dispatch({ type: 'SET_CONNECTION_STATUS', payload: false });
      showToast('error', 'Keine Internetverbindung');
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [state.autoBackupEnabled, state.currentUser]);

  const showToast = (type, message) => {
    dispatch({ type: 'SET_TOAST', payload: { type, message } });
    setTimeout(() => dispatch({ type: 'SET_TOAST', payload: null }), 2000);
  };

  const triggerAutoSave = () => {
    if (!state.autoBackupEnabled) return;
    if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
    autoSaveTimeoutRef.current = setTimeout(() => saveToCloud(true), 3000);
  };

  const logActivity = async (type, details = "", metadata = {}, explicitUser = null) => {
    const initials = explicitUser || state.currentUser?.initials || "SYS";
    const newLog = { id: Date.now() + Math.random(), user: initials, type, details, metadata, timestamp: new Date().toISOString() };
    dispatch({ type: 'ADD_LOG', payload: newLog });
    const currentLogs = await storageService.load(LOGS_KEY) || [];
    await storageService.save(LOGS_KEY, [newLog, ...currentLogs].slice(0, 1000));
    triggerAutoSave();
  };

  const syncLexwareInvoices = async (isAuto = false) => {
    const config = state.lexwareApiConfig;
    if (!config.apiKey) {
      addSyncLog('Fehler: Kein API-Key hinterlegt.', 'error');
      if (!isAuto) showToast('error', 'Lexware API Key fehlt');
      return;
    }

    addSyncLog(`Starte Synchronisation (ab ${config.startDate})...`);
    if (isAuto) dispatch({ type: 'SET_TOAST', payload: { type: 'success', message: 'Lexware Sync läuft...' } });
    else dispatch({ type: 'SET_LOADING', payload: true });

    try {
      addSyncLog('Initialisiere Lexware Service...');
      lexwareService.initialize(config.apiKey);
      
      addSyncLog('Request an Lexware API wird gesendet...');
      // Wir übergeben addSyncLog als Callback für detailliertes Proxy-Feedback
      const newInvoices = await lexwareService.fetchInvoices(
        config.startDate, 
        'any', 
        'invoice', 
        (msg, type) => addSyncLog(msg, type)
      );
      
      addSyncLog(`${newInvoices.length} Rechnungen von API empfangen.`);
      
      addSyncLog('Lade lokale Daten zum Abgleich...');
      const currentSales = await storageService.load(STORAGE_KEY) || [];
      const existingSigs = new Set(currentSales.map(s => `${s['RG-Nr.']}|${s['Kundenummer']}`));
      
      addSyncLog('Prüfe auf Doubletten...');
      const toAdd = newInvoices.filter(s => !existingSigs.has(`${s['RG-Nr.']}|${s['Kundenummer']}`));
      
      if (toAdd.length > 0) {
        addSyncLog(`${toAdd.length} neue Rechnungen identifiziert. Speichere...`);
        const merged = [...currentSales, ...toAdd].sort((a,b) => new Date(b['RG-Datum']) - new Date(a['RG-Datum']));
        await storageService.save(STORAGE_KEY, merged);
        await logActivity('LEXWARE_API_SYNC', `${toAdd.length} Rechnungen per API importiert`);
        addSyncLog('Interne Datenbank aktualisiert.');
        await refreshData();
        triggerAutoSave();
      } else {
        addSyncLog('Keine neuen Rechnungen gefunden.');
      }

      const updatedConfig = { ...config, lastSync: new Date().toISOString() };
      dispatch({ type: 'SET_API_CONFIG', payload: updatedConfig });
      
      addSyncLog('Synchronisation erfolgreich abgeschlossen.', 'success');
      if (!isAuto || toAdd.length > 0) {
        showToast('success', `${toAdd.length} neue Rechnungen geladen`);
      }
    } catch (err) {
      addSyncLog(`Kritischer Fehler: ${err.message}`, 'error');
      console.error('Lexware Sync Error:', err);
      showToast('error', 'Lexware Sync fehlgeschlagen');
    } finally {
      if (!isAuto) dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const refreshData = async () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const [sales, templates, users, logs, contacts, masterData, tasks] = await Promise.all([
        storageService.load(STORAGE_KEY),
        storageService.load(TEMPLATES_KEY),
        storageService.load(USERS_KEY),
        storageService.load(LOGS_KEY),
        storageService.load(CONTACTS_KEY),
        storageService.load(CUSTOMER_MASTER_KEY),
        storageService.load(TASKS_KEY)
      ]);
      const salesData = sales || [];
      const master = masterData || {};
      dispatch({ type: 'SET_CUSTOMER_MASTER', payload: master });
      const customers = extractCustomersFromSales(salesData, master);
      dispatch({ type: 'SET_DATA', payload: {
        sales: salesData,
        customers,
        contacts: contacts || [],
        templates: templates || [],
        users: Array.isArray(users) ? users : [],
        logs: logs || [],
        tasks: tasks || []
      }, isMockData: !sales || sales.length === 0 });
    } catch (e) {
      console.error("Refresh failed", e);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  useEffect(() => { 
    const initOffset = async () => {
      await refreshData();
      if (!initialSyncDone.current) {
        initialSyncDone.current = true;
      }
    };
    initOffset();
  }, []);

  const saveToCloud = async (silent = false) => {
    if (!state.isConnected) return false;
    if (!silent) dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const sales = await storageService.load(STORAGE_KEY);
      const contacts = await storageService.load(CONTACTS_KEY);
      const templates = await storageService.load(TEMPLATES_KEY);
      const users = await storageService.load(USERS_KEY);
      const master = await storageService.load(CUSTOMER_MASTER_KEY);
      const logs = await storageService.load(LOGS_KEY);
      const tasks = await storageService.load(TASKS_KEY);
      
      const apiConfigToSave = { 
        ...state.lexwareApiConfig, 
        apiKey: encrypt(state.lexwareApiConfig.apiKey) 
      };

      const fullData = { 
        version: '5.5', 
        timestamp: new Date().toISOString(), 
        sales, contacts, templates, users, customerMasterData: master, logs, tasks,
        apiConfig: apiConfigToSave 
      };
      
      await s3Service.uploadJson(CLOUD_BACKUP_PATH, fullData, CLOUD_BUCKET);
      dispatch({ type: 'SET_LAST_AUTO_BACKUP', payload: new Date().toISOString() });
      if (!silent) showToast('success', 'Backup erfolgreich gespeichert');
      return true;
    } catch (err) {
      setLastCloudError({ message: err.message, timestamp: new Date().toISOString() });
      showToast('error', 'Backup fehlgeschlagen');
      return false;
    } finally {
      if (!silent) dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const loadFromCloud = async (silent = false) => {
    if (!state.isConnected) return false;
    if (!silent) dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const d = await s3Service.downloadJson(CLOUD_BACKUP_PATH, CLOUD_BUCKET);
      if (d.sales) await storageService.save(STORAGE_KEY, d.sales);
      if (d.contacts) await storageService.save(CONTACTS_KEY, d.contacts);
      if (d.templates) await storageService.save(TEMPLATES_KEY, d.templates);
      if (d.users) await storageService.save(USERS_KEY, d.users);
      if (d.customerMasterData) await storageService.save(CUSTOMER_MASTER_KEY, d.customerMasterData);
      if (d.logs) await storageService.save(LOGS_KEY, d.logs);
      if (d.tasks) await storageService.save(TASKS_KEY, d.tasks);
      
      if (d.apiConfig) {
        const apiConfig = { ...d.apiConfig };
        if (apiConfig.apiKey && apiConfig.apiKey.includes('U2FsdGVkX1')) {
          apiConfig.apiKey = decrypt(apiConfig.apiKey);
        }
        dispatch({ type: 'SET_API_CONFIG', payload: apiConfig });
      }
      
      await refreshData();
      return true;
    } catch (err) {
      return false;
    } finally {
      if (!silent) dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const exportDatabase = async () => {
    const [sales, contacts, templates, users, master, logs, tasks] = await Promise.all([
      storageService.load(STORAGE_KEY),
      storageService.load(CONTACTS_KEY),
      storageService.load(TEMPLATES_KEY),
      storageService.load(USERS_KEY),
      storageService.load(CUSTOMER_MASTER_KEY),
      storageService.load(LOGS_KEY),
      storageService.load(TASKS_KEY)
    ]);
    const apiConfig = { ...state.lexwareApiConfig, apiKey: encrypt(state.lexwareApiConfig.apiKey) };
    return { sales, contacts, templates, users, customerMasterData: master, logs, tasks, apiConfig };
  };

  const restoreBackup = async (d) => {
    if (!d) return;
    if (d.sales) await storageService.save(STORAGE_KEY, d.sales);
    if (d.contacts) await storageService.save(CONTACTS_KEY, d.contacts);
    if (d.templates) await storageService.save(TEMPLATES_KEY, d.templates);
    if (d.users) await storageService.save(USERS_KEY, d.users);
    if (d.customerMasterData) await storageService.save(CUSTOMER_MASTER_KEY, d.customerMasterData);
    if (d.logs) await storageService.save(LOGS_KEY, d.logs);
    if (d.tasks) await storageService.save(TASKS_KEY, d.tasks);
    if (d.apiConfig) {
      const apiConfig = { ...d.apiConfig };
      if (apiConfig.apiKey && apiConfig.apiKey.includes('U2FsdGVkX1')) {
        apiConfig.apiKey = decrypt(apiConfig.apiKey);
      }
      dispatch({ type: 'SET_API_CONFIG', payload: apiConfig });
    }
    await refreshData();
  };

  const value = {
    ...state,
    lastCloudError,
    syncLogs,
    clearSyncLogs,
    syncLexwareInvoices,
    exportDatabase,
    restoreBackup,
    getLastCloudBackupTime: () => state.lastAutoBackup,
    setApiConfig: (config) => dispatch({ type: 'SET_API_CONFIG', payload: config }),
    setCurrentUser: async (user) => {
      dispatch({ type: 'SET_CURRENT_USER', payload: user });
      if (user) await loadFromCloud(true);
    },
    logout: async () => {
      if (state.currentUser) await saveToCloud(true);
      dispatch({ type: 'SET_CURRENT_USER', payload: null });
    },
    importSalesData: async (csvText) => {
      const newSales = parseLexwareCSV(csvText);
      const currentSales = await storageService.load(STORAGE_KEY) || [];
      const existingSigs = new Set(currentSales.map(s => `${s['RG-Nr.']}|${s['Kundenummer']}`));
      const toAdd = newSales.filter(s => !existingSigs.has(`${s['RG-Nr.']}|${s['Kundenummer']}`));
      const merged = [...currentSales, ...toAdd].sort((a,b) => new Date(b['RG-Datum']) - new Date(a['RG-Datum']));
      await storageService.save(STORAGE_KEY, merged);
      await refreshData(); triggerAutoSave();
      return toAdd.length;
    },
    importCustomerData: async (csvText) => {
      const { customers: newMaster, contacts: newCont } = parseCustomerImportCSV(csvText);
      const oldMaster = await storageService.load(CUSTOMER_MASTER_KEY) || {};
      await storageService.save(CUSTOMER_MASTER_KEY, { ...oldMaster, ...newMaster });
      const oldCont = await storageService.load(CONTACTS_KEY) || [];
      const mergedCont = [...oldCont];
      newCont.forEach(nc => { if (!mergedCont.some(c => c.email === nc.email)) mergedCont.push({ ...nc, id: 'cont-' + Date.now() + Math.random() }); });
      await storageService.save(CONTACTS_KEY, mergedCont);
      await refreshData(); triggerAutoSave();
      return { customers: Object.keys(newMaster).length, contacts: newCont.length };
    },
    updateCustomer: async (d) => {
      const old = await storageService.load(CUSTOMER_MASTER_KEY) || {};
      await storageService.save(CUSTOMER_MASTER_KEY, { ...old, [d.id]: d });
      await refreshData(); triggerAutoSave();
    },
    updateContact: async (c) => {
      const list = (state.data.contacts || []).map(item => item.id === c.id ? c : item);
      await storageService.save(CONTACTS_KEY, list);
      await refreshData(); triggerAutoSave();
    },
    deleteContact: async (id) => {
      const list = (state.data.contacts || []).filter(c => c.id !== id);
      await storageService.save(CONTACTS_KEY, list);
      await refreshData(); triggerAutoSave();
    },
    addContact: async (c) => {
      const list = [...(state.data.contacts || []), { ...c, id: 'cont-' + Date.now() }];
      await storageService.save(CONTACTS_KEY, list);
      await refreshData(); triggerAutoSave();
    },
    addTask: async (task) => {
      const newTask = { ...task, id: 'task-' + Date.now(), createdAt: new Date().toISOString(), status: 'PENDING', user: state.currentUser?.initials || 'SYS' };
      await storageService.save(TASKS_KEY, [...(state.data.tasks || []), newTask]);
      await refreshData(); triggerAutoSave();
    },
    updateTask: async (ut) => {
      const list = (state.data.tasks || []).map(t => t.id === ut.id ? ut : t);
      await storageService.save(TASKS_KEY, list);
      await refreshData(); triggerAutoSave();
    },
    deleteTask: async (id) => {
      await storageService.save(TASKS_KEY, (state.data.tasks || []).filter(t => t.id !== id));
      await refreshData(); triggerAutoSave();
    },
    updateUsers: async (u) => { await storageService.save(USERS_KEY, u); await refreshData(); triggerAutoSave(); },
    updateTemplates: async (t) => { await storageService.save(TEMPLATES_KEY, t); await refreshData(); triggerAutoSave(); },
    deleteLog: async (id) => { await storageService.save(LOGS_KEY, (state.data.logs || []).filter(l => l.id !== id)); await refreshData(); triggerAutoSave(); },
    updateLog: async (ul) => { await storageService.save(LOGS_KEY, (state.data.logs || []).map(l => l.id === ul.id ? ul : l)); await refreshData(); triggerAutoSave(); },
    clearStorage: async () => {
      await Promise.all([STORAGE_KEY, CONTACTS_KEY, TEMPLATES_KEY, USERS_KEY, LOGS_KEY, TASKS_KEY, CUSTOMER_MASTER_KEY].map(k => storageService.remove(k)));
      await refreshData(); triggerAutoSave();
    },
    saveToCloud, loadFromCloud, logActivity, showToast, toggleAutoBackup: (e) => dispatch({ type: 'TOGGLE_AUTO_BACKUP', payload: e })
  };

  return <LexwareContext.Provider value={value}>{children}</LexwareContext.Provider>;
}

export const useLexware = () => useContext(LexwareContext);